import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AccountContext } from '../useContext/Account';

/* Estado compartido del marco del panel: el menú del usuario (carpetas y opciones que trae
   /usuarioopciones), favoritos, pantallas recientes, menú contraído y la paleta «Ir a…» (Ctrl+K).
   Favoritos, recientes y el menú contraído se guardan en el navegador, por usuario. */

const MarcoContext = createContext(null);

export const normalizarRuta = (ruta = '') => '/' + String(ruta).trim().replace(/^\/+/, '');
const claveRuta = (ruta) => normalizarRuta(ruta).toLowerCase();

const leer = (clave, defecto) => {
  try {
    const v = window.localStorage.getItem(clave);
    return v === null ? defecto : JSON.parse(v);
  } catch (e) {
    return defecto;
  }
};
const guardar = (clave, valor) => {
  try { window.localStorage.setItem(clave, JSON.stringify(valor)); } catch (e) { /* sin almacenamiento: no pasa nada */ }
};

export const RUTA_INICIO = '/Dashboard';
const MAX_RECIENTES = 8;
const MAX_FAVORITOS = 9;   // Alt+1 … Alt+9

export function MarcoProvider({ children }) {
  const { getUser } = useContext(AccountContext);
  const user = getUser();
  const usuario = user && user.username ? user.username.toUpperCase() : '';
  const location = useLocation();

  const [carpetas, setCarpetas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorMenu, setErrorMenu] = useState('');
  const [favoritos, setFavoritos] = useState([]);
  const [recientes, setRecientes] = useState([]);
  const [contraido, setContraido] = useState(false);
  const [paletaAbierta, setPaletaAbierta] = useState(false);

  // preferencias guardadas de este usuario
  useEffect(() => {
    if (!usuario) return;
    setFavoritos(leer(`sym:${usuario}:favoritos`, []));
    setRecientes(leer(`sym:${usuario}:recientes`, []));
    setContraido(leer('sym:menu-contraido', false));
  }, [usuario]);

  const cargarMenu = useCallback(async () => {
    if (!usuario) return;
    setCargando(true);
    setErrorMenu('');
    try {
      const r = await fetch(`${process.env.REACT_APP_ENDPOINT_DATA}/usuarioopciones/find/${encodeURIComponent(usuario)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCarpetas(Array.isArray(data.carpetas) ? data.carpetas : []);
    } catch (e) {
      console.error('Error cargando el menú:', e);
      setErrorMenu('No se pudo cargar el menú. Revise la conexión.');
    } finally {
      setCargando(false);
    }
  }, [usuario]);

  useEffect(() => { cargarMenu(); }, [cargarMenu]);

  // lista plana de opciones: { carpeta, nombre, ruta }
  const opciones = useMemo(() => {
    const out = [];
    const vistas = new Set();
    carpetas.forEach((c) => (c.opciones || []).forEach((o) => {
      if (!o || !o.ruta) return;
      const ruta = normalizarRuta(o.ruta);
      if (vistas.has(ruta.toLowerCase())) return;
      vistas.add(ruta.toLowerCase());
      out.push({ carpeta: c.carpeta, nombre: o.nombre || o.opcion || ruta.slice(1), ruta });
    }));
    return out;
  }, [carpetas]);

  const opcionPorRuta = useCallback(
    (ruta) => opciones.find((o) => o.ruta.toLowerCase() === claveRuta(ruta)) || null,
    [opciones]
  );
  const opcionActual = opcionPorRuta(location.pathname);

  // pantallas recientes (solo las que están en el menú del usuario)
  useEffect(() => {
    if (!usuario || !opcionActual) return;
    setRecientes((prev) => {
      const sig = [opcionActual.ruta, ...prev.filter((r) => claveRuta(r) !== claveRuta(opcionActual.ruta))].slice(0, MAX_RECIENTES);
      guardar(`sym:${usuario}:recientes`, sig);
      return sig;
    });
  }, [usuario, opcionActual]);

  const esFavorito = useCallback((ruta) => favoritos.some((f) => claveRuta(f) === claveRuta(ruta)), [favoritos]);

  const alternarFavorito = useCallback((ruta) => {
    setFavoritos((prev) => {
      const ya = prev.some((f) => claveRuta(f) === claveRuta(ruta));
      const sig = ya ? prev.filter((f) => claveRuta(f) !== claveRuta(ruta)) : [...prev, normalizarRuta(ruta)].slice(-MAX_FAVORITOS);
      guardar(`sym:${usuario}:favoritos`, sig);
      return sig;
    });
  }, [usuario]);

  const alternarMenu = useCallback(() => {
    setContraido((v) => { guardar('sym:menu-contraido', !v); return !v; });
  }, []);

  // favoritos y recientes como opciones (descarta los que ya no estén en el menú del usuario)
  const opcionesFavoritas = useMemo(() => favoritos.map(opcionPorRuta).filter(Boolean), [favoritos, opcionPorRuta]);
  const opcionesRecientes = useMemo(() => recientes.map(opcionPorRuta).filter(Boolean), [recientes, opcionPorRuta]);

  const valor = {
    usuario,
    carpetas,
    opciones,
    cargando,
    errorMenu,
    cargarMenu,
    opcionActual,
    opcionPorRuta,
    opcionesFavoritas,
    opcionesRecientes,
    esFavorito,
    alternarFavorito,
    contraido,
    alternarMenu,
    paletaAbierta,
    abrirPaleta: () => setPaletaAbierta(true),
    cerrarPaleta: () => setPaletaAbierta(false),
  };

  return <MarcoContext.Provider value={valor}>{children}</MarcoContext.Provider>;
}

export const useMarco = () => useContext(MarcoContext);
